// Pure Zeit-Mathematik des „Zeiten"-Tabs — I/O-frei und einzeln getestet. Netto-Arbeitszeit/Beginn/Ende
// kommen bewusst NICHT von hier, sondern fertig aus dem Backend-Report (@wf/shared, B-5-Mitternachts-Split);
// hier lebt nur, was die UI zusätzlich braucht: Pausen-Anzeige, ISO-Wochen (>48h-Warnung), Monats-Navigation
// und die Formular-Konvertierungen (Dienstplan-Minuten, Nachtrag-Zeitstempel).
//
// ⚠️ Zeitzonen-Regel (Review-Fund 07/2026): Die Server-Tageszeilen sind fest auf die Werkstatt-Zeitzone
// (DEFAULT_TIME_ZONE, Europe/Berlin) gekeyt. ALLES hier rechnet daher über die tz-fixierten @wf/shared-
// Helfer (localDayKey/wallTimeToUtcMs) — NIE über die Geräte-Zeitzone (localYmd/new Date(`${day}T…`)),
// sonst rutschen Chips/Pausen/CSV auf falsche Tage, sobald ein Tablet nicht auf Berlin steht.
import { localDayKey, wallTimeToUtcMs, type AttendanceDay, type AttendanceReportStamp } from '@wf/shared'

/**
 * Pausensekunden je Werkstatt-Lokaltag aus geschlossenen pause_start→pause_ende-Paaren. Entwertete
 * Stempel und offene Pausen zählen nicht (die Netto-Zeit rechnet ohnehin der Server — reine Anzeige).
 */
export function pauseSecondsByDay(stamps: readonly AttendanceReportStamp[], timeZone?: string): Record<string, number> {
  const result: Record<string, number> = {}
  let openStart: number | null = null
  for (const s of [...stamps].sort((a, b) => a.tsMs - b.tsMs)) {
    if (s.voided) continue
    if (s.kind === 'pause_start') {
      openStart = s.tsMs
    } else if (s.kind === 'pause_ende' && openStart != null && s.tsMs > openStart) {
      const day = localDayKey(openStart, timeZone)
      result[day] = (result[day] ?? 0) + Math.floor((s.tsMs - openStart) / 1000)
      openStart = null
    } else if (s.kind === 'kommen' || s.kind === 'gehen') {
      openStart = null // Pause endet spätestens mit dem nächsten Arbeits-Stempel — halb offene Paare verwerfen
    }
  }
  return result
}

/** ISO-8601-Kalenderwoche ('YYYY-Wnn') eines lokalen Kalendertags — für die >48h/Woche-Warnung (ArbZG). */
export function isoWeekKey(day: string): string {
  // Mittags-Anker vermeidet DST-Kanten; Donnerstag derselben ISO-Woche bestimmt das Wochenjahr.
  const date = new Date(day + 'T12:00:00')
  const thursday = new Date(date)
  thursday.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7))
  const yearStart = new Date(thursday.getFullYear(), 0, 4)
  const week = Math.round(((thursday.getTime() - yearStart.getTime()) / 86_400_000 + ((yearStart.getDay() + 6) % 7) + 1) / 7)
  return `${thursday.getFullYear()}-W${String(week).padStart(2, '0')}`
}

/** Arbeitssekunden je ISO-Woche aus den Report-Tageszeilen (Server-Wahrheit, nicht neu gepaart). */
export function weekSecondsFromDays(days: readonly AttendanceDay[]): Record<string, number> {
  const result: Record<string, number> = {}
  for (const d of days) {
    const key = isoWeekKey(d.day)
    result[key] = (result[key] ?? 0) + d.workedSeconds
  }
  return result
}

/** Erster/letzter Kalendertag eines Monats ('YYYY-MM' → inklusives from/to für den Report). */
export function monthRange(ym: string): { from: string; to: string } {
  const [year, month] = ym.split('-').map(Number) as [number, number]
  const lastDay = new Date(year, month, 0).getDate() // Tag 0 des Folgemonats = letzter Tag dieses Monats
  return { from: `${ym}-01`, to: `${ym}-${String(lastDay).padStart(2, '0')}` }
}

/** Kalendertag ± n Tage (reine Datums-Arithmetik über UTC-Mittag, tz-neutral). */
export function shiftDay(day: string, delta: number): string {
  const date = new Date(Date.parse(day + 'T12:00:00Z') + delta * 86_400_000)
  return date.toISOString().slice(0, 10)
}

/** Mo-beginnende Kalenderwoche, die `day` enthält ('YYYY-MM-DD' → inklusives Mo..So). */
export function weekRange(day: string): { from: string; to: string } {
  const date = new Date(day + 'T12:00:00Z')
  const monday = shiftDay(day, -((date.getUTCDay() + 6) % 7))
  return { from: monday, to: shiftDay(monday, 6) }
}

/**
 * Monats-Zeitraum ±6 Tage Puffer: die >48h/Woche-Warnung (ArbZG) braucht VOLLE ISO-Wochen — ohne Puffer
 * ist jede Monatsrand-Woche systematisch unterzählt und die Warnung blind (Review-Fund 07/2026).
 * Die Tabelle/CSV filtert wieder auf den echten Monat; nur die Wochen-Summen nutzen den Puffer.
 */
export function paddedMonthRange(ym: string): { from: string; to: string } {
  const { from, to } = monthRange(ym)
  return { from: shiftDay(from, -6), to: shiftDay(to, 6) }
}

/** Monat verschieben ('YYYY-MM' ± delta, rollt über Jahresgrenzen). */
export function shiftMonth(ym: string, delta: number): string {
  const [year, month] = ym.split('-').map(Number) as [number, number]
  const date = new Date(year, month - 1 + delta, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']

/** 'YYYY-MM' → 'Juli 2026' (Kopfzeile der Monats-Navigation). */
export function monthLabel(ym: string): string {
  const [year, month] = ym.split('-').map(Number) as [number, number]
  return `${MONTH_NAMES[month - 1] ?? ym} ${year}`
}

/** Dienstplan-Minuten ab Mitternacht → 'HH:MM' fürs time-Input (null/leer → ''). */
export function minToHHMM(min: number | null | undefined): string {
  if (min == null || min < 0) return ''
  const h = Math.floor(min / 60)
  const m = min % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** 'HH:MM' aus dem time-Input → Minuten ab Mitternacht (leer/ungültig → null = Tag löschen). */
export function hhmmToMin(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim())
  if (!m) return null
  const min = Number(m[1]) * 60 + Number(m[2])
  return Number.isFinite(min) ? min : null
}

/**
 * WERKSTATT-Wandzeit (Tag + 'HH:MM') → voller ISO-8601-Zeitstempel (mit Z). Der Server parst per
 * `Date.parse`; ein nacktes Datum ohne Offset wäre mehrdeutig (Handoff-W9-Warnung). Bewusst über die
 * tz-fixierte Umrechnung aus @wf/shared — die Geräte-Zeitzone des Chefs darf den Nachtrag nicht verschieben.
 */
export function localTimeToIso(day: string, time: string, timeZone?: string): string | null {
  if (!day || !time) return null
  const ms = wallTimeToUtcMs(day, time, timeZone)
  return Number.isNaN(ms) ? null : new Date(ms).toISOString()
}
